import { AbsenceRequestPolicySummary } from './AbsenceRequestPolicySummary';
import { AbsenceSettingsDisclosure } from './AbsenceSettingsDisclosure';
import { AbsenceRequestConditionFields, AbsenceConditionCheckSummary } from './AbsenceRequestConditionFields';
import { conditionFactsErrors, conditionFactsKey, conditionFactsSummary } from './absenceRequestConditions';
import { AbsenceUsageLimitPreview, AbsenceUsageLimitSummary, exceedsUsageLimit } from './AbsenceUsageLimitSummary';
import { absenceDayPartQuantity, absenceDayPartTimeLabel } from './absenceDayPart';
import { useCalendarKeyboard } from './useCalendarKeyboard';
import {
  CalendarMonthOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  TodayOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  FormTextField,
  Field,
  AttachmentItem,
  FlowSteps,
  DefinitionList,
  DialogSurface,
  LoadingIndicator,
  SegmentedControl,
  StatusBadge,
  createFocusRingStyles,
  designTokens,
} from '../../design-system';
import type {
  AbsenceDayPart,
  AbsenceConditionFacts,
  AbsenceConditionPolicy,
  AbsenceTimeOffBalance as TimeOffBalance,
  AbsenceTimeOffRequestCalculation as TimeOffRequestCalculation,
  AbsenceRequestableTimeOffType as TimeOffType,
} from './model';
import { absencePeriodLimitError } from './absenceRequestPeriod';
import { absenceRequestBalancePreview } from './absenceRequestBalancePreview';
import { dateError, numberError } from './absenceFormValues';
import { useAbsenceFormValidation } from './useAbsenceFormValidation';
import { absenceTypeValidationFields, type AbsenceRequestField } from './absenceRequestValidation';
import { absenceDayCountExplanation } from './absenceDayCountExplanation';
import type { AbsencePort } from './port';
import { PublicHolidayNotice } from './PublicHolidayNotice';
import { usePublicHolidayDates } from './usePublicHolidayDates';
import type { AbsenceEvidenceSelection } from './AbsenceEvidenceField';

type RequestStep = 'dates' | 'review';

const twoMonthCalendarQuery = `@container (min-width: ${designTokens.layout.touchTargetMinSize * 7 * 2 + designTokens.space.xl}px)`;

export type SelfAbsenceRequestDialogProps = {
  port: AbsencePort;
  fixtureMode: boolean;
  open: boolean;
  timeOffType?: TimeOffType;
  timeOffTypes: TimeOffType[];
  onTimeOffTypeChange: (id: string) => void;
  balance?: Pick<TimeOffBalance, 'available' | 'balance'>;
  startDate: string;
  endDate: string;
  quantity: string;
  quantityOverride: boolean;
  dayPart?: AbsenceDayPart;
  onDayPartChange: (value: AbsenceDayPart) => void;
  conditionPolicy: AbsenceConditionPolicy;
  conditionFacts: AbsenceConditionFacts;
  onConditionFactsChange: (facts: AbsenceConditionFacts) => void;
  reason: string;
  evidenceField?: (validation: ReturnType<ReturnType<typeof useAbsenceFormValidation>['field']>) => ReactNode;
  evidenceDocuments: string;
  evidencePending?: boolean;
  evidenceSelection?: AbsenceEvidenceSelection;
  evidenceVerified?: boolean;
  evidenceVerificationMessage?: string;
  calculation?: TimeOffRequestCalculation;
  calculationLoading: boolean;
  calculationError?: string;
  calculationFieldError?: { field: AbsenceRequestField; message: string };
  onRetryCalculation?: () => void;
  initialStep?: RequestStep;
  submitError?: string;
  submitLoading: boolean;
  fieldsLocked?: boolean;
  savedRequest?: boolean;
  closeDisabled?: boolean;
  onClose: () => void;
  retainDraft?: boolean;
  onDiscard?: () => void;
  onDatesChange: (startDate: string, endDate: string) => void;
  onUseAutomaticQuantity: () => void;
  onQuantityChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onSubmit: () => void;
};

function parseDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function formatDate(date: Date): string {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

function localToday(): string {
  const now = new Date();
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
}

function monthStart(value: string | Date): Date {
  const date = typeof value === 'string' ? parseDate(value) : value;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addMonths(date: Date, amount: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1));
}

function dateLabel(value: string): string {
  if (!value) return '';
  const date = parseDate(value);
  return `${date.getUTCFullYear()}년 ${date.getUTCMonth() + 1}월 ${date.getUTCDate()}일`;
}

function periodLabel(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '날짜를 선택해 주세요.';
  return startDate === endDate ? dateLabel(startDate) : `${dateLabel(startDate)} – ${dateLabel(endDate)}`;
}

function quantityLabel(quantity: string | undefined, unit: string | undefined): string {
  if (!quantity) return '-';
  const normalized = unit?.toUpperCase() ?? '';
  return `${Number(quantity).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}${normalized.includes('HOUR') ? '시간' : normalized.includes('MINUTE') ? '분' : '일'}`;
}

function MonthCalendar({ month, startDate, endDate, publicHolidays, onSelect, keyboard }: { keyboard: ReturnType<typeof useCalendarKeyboard>; month: Date; startDate: string; endDate: string; publicHolidays: ReadonlyMap<string, string>; onSelect: (value: string) => void }) {
  const year = month.getUTCFullYear();
  const monthIndex = month.getUTCMonth();
  const firstWeekday = new Date(Date.UTC(year, monthIndex, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const cells = Array.from({ length: firstWeekday + daysInMonth }, (_, index) => index < firstWeekday ? undefined : formatDate(new Date(Date.UTC(year, monthIndex, index - firstWeekday + 1))));
  const today = localToday();

  return <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" aria-live="polite" sx={{ mb: 1.25, textAlign: 'center', ...designTokens.typography.subsectionTitle }}>{year}년 {monthIndex + 1}월</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', rowGap: 0.45 }}>
      {['일', '월', '화', '수', '목', '금', '토'].map((weekday, index) => <Typography key={weekday} variant="caption" sx={{ py: 0.4, textAlign: 'center', color: index === 0 ? designTokens.color.dangerText : index === 6 ? designTokens.color.infoDark : designTokens.color.secondaryText }}>{weekday}</Typography>)}
      {cells.map((value, index) => value ? (() => {
        const selectedEdge = value === startDate || value === endDate;
        const inRange = Boolean(startDate && endDate && value >= startDate && value <= endDate);
        const publicHoliday = publicHolidays.get(value);
        const weekday = parseDate(value).getUTCDay();
        const startsBand = weekday === 0 || value.endsWith('-01');
        const endsBand = weekday === 6 || Number(value.slice(-2)) === daysInMonth;
        return <Box key={value} sx={{
          position: 'relative',
          ...(inRange && startDate !== endDate ? { '&::before': {
            content: '""', position: 'absolute', insetBlock: 0,
            insetInlineStart: value === startDate ? '50%' : 0,
            insetInlineEnd: value === endDate ? '50%' : 0,
            bgcolor: designTokens.color.actionSoft,
            borderStartStartRadius: startsBand ? designTokens.radius.pill : 0,
            borderEndStartRadius: startsBand ? designTokens.radius.pill : 0,
            borderStartEndRadius: endsBand ? designTokens.radius.pill : 0,
            borderEndEndRadius: endsBand ? designTokens.radius.pill : 0,
            '@media (forced-colors: active)': { bgcolor: 'Highlight' },
          } } : {}),
        }}>
          <Tooltip title={publicHoliday ? `공휴일 · ${publicHoliday}` : ''} describeChild>
          <ButtonBase
            {...keyboard.dayProps(value)}
            aria-current={value === today ? 'date' : undefined}
            aria-label={`${dateLabel(value)}${publicHoliday ? `, 공휴일 ${publicHoliday}` : ''}`}
            aria-pressed={inRange}
            disableRipple
            onClick={() => onSelect(value)}
            sx={{
              width: '100%',
              minWidth: 0,
              height: designTokens.layout.controlMinHeight,
              [designTokens.media.touchTarget]: {
                height: designTokens.layout.touchTargetMinSize,
              },
              mx: 'auto',
              display: 'grid',
              placeItems: 'center',
              borderRadius: designTokens.radius.navigation,
              color: publicHoliday || weekday === 0 ? designTokens.color.dangerText : weekday === 6 ? designTokens.color.infoDark : selectedEdge ? designTokens.color.actionDark : designTokens.color.primaryText,
              ...designTokens.typography.ui,
              fontWeight: selectedEdge ? 600 : 400,
              '&:hover > span': { bgcolor: selectedEdge ? designTokens.color.selectedHoverSurface : designTokens.color.hoverSurface },
              '&:focus-visible, &.Mui-focusVisible': createFocusRingStyles({ inset: true }),
              '@media (forced-colors: active)': {
                forcedColorAdjust: 'none',
                color: inRange ? 'HighlightText' : 'ButtonText',
                '&:hover > span': { bgcolor: inRange ? 'Highlight' : 'Canvas' },
                '&:focus-visible, &.Mui-focusVisible': {
                  outlineColor: inRange ? 'HighlightText' : 'Highlight',
                  outlineOffset: -4,
                },
              },
            }}
          ><Box component="span" sx={{
            position: 'relative', display: 'grid', placeItems: 'center',
            width: designTokens.layout.controlMinHeight, height: designTokens.layout.controlMinHeight,
            [designTokens.media.touchTarget]: { width: designTokens.layout.touchTargetMinSize, height: designTokens.layout.touchTargetMinSize },
            borderRadius: designTokens.radius.circle,
            bgcolor: selectedEdge ? designTokens.color.compactSelectedSurface : 'transparent',
            border: value === today && !selectedEdge ? '1px solid' : undefined,
            borderColor: designTokens.color.brandBorder,
            '@media (forced-colors: active)': {
              bgcolor: inRange ? 'Highlight' : 'Canvas',
              border: '1px solid',
              borderColor: selectedEdge ? 'HighlightText' : value === today ? 'ButtonText' : 'transparent',
            },
          }}>{Number(value.slice(-2))}</Box></ButtonBase>
          </Tooltip>
        </Box>;
      })() : <Box key={`empty-${index}`} />)}
    </Box>
  </Box>;
}

function RequestCalendars({ port, fixtureMode, visibleMonth, startDate, endDate, onSelect, onNavigate }: { onNavigate: (date: string) => void; port: AbsencePort; fixtureMode: boolean; visibleMonth: Date; startDate: string; endDate: string; onSelect: (value: string) => void }) {
  const from = formatDate(visibleMonth);
  const to = formatDate(new Date(Date.UTC(visibleMonth.getUTCFullYear(), visibleMonth.getUTCMonth() + 2, 0)));
  const publicHolidayDates = usePublicHolidayDates(port, fixtureMode, from, to);
  const publicHolidays = publicHolidayDates.byDate;
  const keyboard = useCalendarKeyboard({ from, to, initialDate: startDate || localToday(), onNavigate });
  return <>
    <PublicHolidayNotice resource={publicHolidayDates} inset={false} />
    <Box role="status" sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}>{periodLabel(startDate, endDate)}</Box>
    <Box ref={keyboard.containerRef} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: `${designTokens.space.xl}px`, [twoMonthCalendarQuery]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } }}>
      <MonthCalendar month={visibleMonth} startDate={startDate} endDate={endDate} publicHolidays={publicHolidays} onSelect={onSelect} keyboard={keyboard} />
      <Box sx={{ display: 'none', [twoMonthCalendarQuery]: { display: 'block' } }}><MonthCalendar month={addMonths(visibleMonth, 1)} startDate={startDate} endDate={endDate} publicHolidays={publicHolidays} onSelect={onSelect} keyboard={keyboard} /></Box>
    </Box>
  </>;
}

export function SelfAbsenceRequestDialog({
  port,
  fixtureMode,
  open,
  timeOffType,
  timeOffTypes,
  onTimeOffTypeChange,
  balance,
  startDate,
  endDate,
  quantity,
  quantityOverride,
  dayPart,
  onDayPartChange,
  conditionPolicy,
  conditionFacts,
  onConditionFactsChange,
  reason,
  evidenceField,
  evidenceDocuments,
  evidencePending = false,
  evidenceSelection,
  evidenceVerified = true,
  evidenceVerificationMessage,
  calculation,
  calculationLoading,
  calculationError,
  calculationFieldError,
  onRetryCalculation,
  initialStep = 'dates',
  submitError,
  submitLoading,
  fieldsLocked = false,
  savedRequest = false,
  closeDisabled = false,
  onClose,
  retainDraft = false,
  onDiscard,
  onDatesChange,
  onUseAutomaticQuantity,
  onQuantityChange,
  onReasonChange,
  onSubmit,
}: SelfAbsenceRequestDialogProps) {
  const [step, setStep] = useState<RequestStep>('dates');
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => monthStart(localToday()));
  const progressHintId = useId();
  const dayPartControlId = useId();
  const workspaceRef = useRef<HTMLFieldSetElement>(null);
  const reviewTitleRef = useRef<HTMLHeadingElement>(null);
  const reviewSectionRef = useRef<HTMLDivElement>(null);
  const requestTypeRef = useRef<HTMLDivElement>(null);
  const evidenceRef = useRef<HTMLDivElement>(null);
  const [pendingFocus, setPendingFocus] = useState<'dates' | 'review' | 'evidence' | null>(null);
  const controlsLocked = submitLoading || fieldsLocked;

  useEffect(() => {
    setStep(initialStep);
    setSelectingEnd(false);
    setVisibleMonth(monthStart(startDate || localToday()));
  }, [timeOffType?.id, initialStep]);

  const focusStep = (target: 'dates' | 'review' | 'evidence') => {
    const section = target === 'review' ? reviewTitleRef.current : target === 'evidence' ? evidenceRef.current : requestTypeRef.current;
    if (!section) return;
    (target === 'review' ? reviewSectionRef.current : section)?.scrollIntoView({ block: 'start', behavior: 'auto' });
    const control = target === 'review' ? section : section.querySelector<HTMLElement>('[role="combobox"]') ?? section;
    control.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (!open || !pendingFocus) return;
    focusStep(pendingFocus); setPendingFocus(null);
  }, [open, step, pendingFocus]);
  const changeStep = (next: RequestStep, focus: 'dates' | 'review' | 'evidence' = next) => { setStep(next); setPendingFocus(focus); };

  const handleSelectDate = (value: string) => {
    if (controlsLocked) return;
    if (!startDate || !selectingEnd) {
      onDatesChange(value, value);
      setSelectingEnd(true);
      return;
    }
    onDatesChange(value < startDate ? value : startDate, value < startDate ? startDate : value);
    setSelectingEnd(false);
  };

  const handleDateInput = (value: string, boundary: 'start' | 'end') => {
    onDatesChange(boundary === 'start' ? value : startDate, boundary === 'end' ? value : endDate);
    setSelectingEnd(false);
    if (value) setVisibleMonth(monthStart(value));
  };

  const normalizedUnit = timeOffType?.allowedUnit.toUpperCase() ?? '';
  const periodLimitError = absencePeriodLimitError(startDate, endDate, timeOffType?.maximumConsecutiveDays);
  const dateRangeError = startDate && endDate && endDate < startDate ? '종료일은 시작일과 같거나 이후여야 합니다.' : periodLimitError;
  const sameDay = Boolean(startDate && startDate === endDate);
  const supportsHalfDay = normalizedUnit.includes('DAY') && timeOffType?.usageMode === 'SPLIT' && Number(timeOffType.minimumIncrement) <= 0.5;
  const supportsQuarterDay = supportsHalfDay && Number(timeOffType?.minimumIncrement) <= 0.25;
  const canUseHalfDay = sameDay && supportsHalfDay;
  const selectedHalfDay = canUseHalfDay && quantityOverride && (Number(quantity) === 0.5 || Number(quantity) === 0.25);
  const calculationMatchesSelection = Boolean(calculation && timeOffType && calculation.timeOffType.id === timeOffType.id && calculation.startDate === startDate && calculation.endDate === endDate && calculation.unitOfTime === timeOffType.allowedUnit && Number(calculation.quantity) === Number(quantity) && (calculation.dayPart || 'FULL_DAY') === (dayPart || 'FULL_DAY') && conditionFactsKey(calculation.conditionFacts) === conditionFactsKey(conditionFacts));
  const verifiedCalculation = calculationMatchesSelection && !calculationLoading && !calculationError && !dateRangeError ? calculation : undefined;
  const limitBased = timeOffType?.managementMode === 'USAGE_LIMIT';
  const limitExceeded = exceedsUsageLimit(verifiedCalculation);
  const available = verifiedCalculation?.available ?? (!startDate && !endDate ? balance?.available ?? balance?.balance : undefined);
  const summaryUnit = verifiedCalculation?.unitOfTime || timeOffType?.allowedUnit;
  const projectedRemaining = absenceRequestBalancePreview(verifiedCalculation);
  const usesAdvance = projectedRemaining !== undefined && Number(projectedRemaining) < 0 && verifiedCalculation?.advanceAllowed;
  const usageLabel = selectedHalfDay
    ? absenceDayPartTimeLabel(dayPart) || '-'
    : normalizedUnit.includes('HOUR') ? '시간 단위' : normalizedUnit.includes('MINUTE') ? '분 단위' : '종일';
  const { period: conditionPeriodError, ...conditionErrors } = conditionFactsErrors(conditionPolicy, conditionFacts, startDate, endDate);
  const fieldErrors = {
    timeOffType: !timeOffType ? '휴가 유형을 선택해 주세요.' : undefined,
    startDate: !startDate ? '휴가 시작일을 선택해 주세요.' : dateError(startDate) || (timeOffType?.availableFrom && startDate < timeOffType.availableFrom ? `${dateLabel(timeOffType.availableFrom)}부터 사용할 수 있습니다.` : undefined),
    endDate: !endDate ? '휴가 종료일을 선택해 주세요.' : dateError(endDate) || dateRangeError || conditionPeriodError || (limitExceeded ? '신청 가능량을 초과한 기간이 있습니다. 종료일이나 사용 단위를 조정해 주세요.' : undefined),
    quantity: normalizedUnit.includes('HOUR') ? numberError(quantity, { positive: true }) : undefined,
    dayPart: selectedHalfDay && absenceDayPartQuantity(dayPart) !== Number(quantity) ? '사용할 반차 시간대를 선택해 주세요.' : undefined,
    ...conditionErrors,
    reason: timeOffType?.reasonRequired && !reason.trim() ? '상세 사유를 입력해 주세요.' : undefined,
    evidenceDocuments: calculation?.evidenceRequired && !evidenceDocuments.trim() ? '신청 전에 증빙 문서를 첨부해 주세요.' : undefined,
    ...(calculationFieldError ? { [calculationFieldError.field]: calculationFieldError.message } : {}),
  };
  const hasCalculationInputErrors = !savedRequest && Object.entries(fieldErrors).some(([name, error]) => name !== 'reason' && name !== 'evidenceDocuments' && Boolean(error));
  const validation = useAbsenceFormValidation(open, savedRequest ? {} : fieldErrors, '', { key: `${timeOffType?.id}:${conditionPolicy.requestCondition}`, fields: absenceTypeValidationFields });
  const field = (name: string) => validation.field(name, undefined, !savedRequest && calculationFieldError?.field === name ? calculationFieldError.message : undefined);
  const close = () => { if (!submitLoading && !closeDisabled) onClose(); };
  const choosingDates = step === 'dates';
  const needsEvidence = Boolean(calculation?.evidenceRequired || calculationFieldError?.field === 'evidenceDocuments' || timeOffType?.evidencePolicy && timeOffType.evidencePolicy !== 'NONE' || evidenceDocuments.trim() || evidencePending);
  const hasRequestCondition = Boolean(conditionPolicy.requestCondition && conditionPolicy.requestCondition !== 'NONE');
  const progressBlocker = savedRequest || hasCalculationInputErrors || calculationFieldError ? undefined
    : calculationError ? '계산 오류를 확인한 뒤 다시 계산해 주세요.'
    : calculationLoading || !calculationMatchesSelection ? '사용량과 사용 가능 여부를 확인하고 있습니다.'
    : verifiedCalculation?.conditionCheck?.eligible === false ? verifiedCalculation.conditionCheck.messages?.[0] || '신청 조건을 확인해 주세요.'
    : hasRequestCondition && !verifiedCalculation?.conditionCheck ? '신청 조건을 확인하지 못했습니다. 다시 계산해 주세요.'
    : limitBased && !verifiedCalculation?.usageLimits?.length ? '신청 가능 한도를 확인하지 못했습니다. 다시 계산해 주세요.'
    : evidencePending ? '첨부 중인 문서의 업로드·보안 검사를 기다리거나 첨부를 취소해 주세요.'
    : !evidenceVerified ? evidenceVerificationMessage || '선택한 증빙을 다시 확인해 주세요.'
    : undefined;
  const submitDisabled = submitLoading || evidencePending || Boolean(progressBlocker);
  const dayCountExplanation = absenceDayCountExplanation(verifiedCalculation);
  const usageQuantity = <Box role="status" aria-live="polite" aria-busy={calculationLoading} aria-label={calculationLoading ? '사용량과 사용 가능 여부를 확인하고 있습니다.' : undefined}>
    {calculationLoading ? <LoadingIndicator label="사용량과 사용 가능 여부를 확인하고 있습니다." /> : <Typography variant="body2" sx={{ fontWeight: 600 }}>{quantityLabel(verifiedCalculation?.quantity, summaryUnit)}</Typography>}
    {dayCountExplanation && <Typography variant="caption" color="text.secondary">{dayCountExplanation}</Typography>}
  </Box>;
  const requestSummary = limitBased ? verifiedCalculation?.usageLimits?.length ? <Stack spacing={0.5}>
    <AbsenceUsageLimitPreview limits={verifiedCalculation.usageLimits} />
    {dayCountExplanation && <Typography variant="caption" color="text.secondary">{dayCountExplanation}</Typography>}
  </Stack> : <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Typography variant="body2" color="text.secondary">사용 예정</Typography>{usageQuantity}</Stack> : <Box component="aside" aria-label="선택한 휴가" sx={{
    display: 'flex', flexWrap: 'wrap', minHeight: designTokens.layout.controlMinHeight,
    gap: `${designTokens.space.sm}px`, alignItems: 'center',
  }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: `${designTokens.space.xs}px` }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${designTokens.space.xs}px` }}>
      <Typography variant="body2" color="text.secondary">사용 예정</Typography>
      {usageQuantity}
    </Box>
    <Box component="dl" sx={{ display: 'flex', flexWrap: 'wrap', gap: `${designTokens.space.xs}px`, m: 0 }}>
      <Box component="div" sx={{ display: 'flex', alignItems: 'baseline', gap: `${designTokens.space.xs}px` }}>
        <Typography component="span" variant="body2" aria-hidden="true" color="text.secondary">·</Typography>
        <Typography component="dt" variant="body2" color="text.secondary">사용 가능</Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0, ml: 'auto' }}>{quantityLabel(available, summaryUnit)}</Typography>
      </Box>
      {verifiedCalculation?.pending !== undefined && <Box component="div" sx={{ display: 'flex', alignItems: 'baseline', gap: `${designTokens.space.xs}px` }}>
        <Typography component="span" variant="body2" aria-hidden="true" color="text.secondary">·</Typography>
        <Typography component="dt" variant="body2" color="text.secondary">승인 대기</Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0, ml: 'auto' }}>{quantityLabel(verifiedCalculation.pending, summaryUnit)}</Typography>
      </Box>}
    </Box>
    </Box>
  </Box>;
  const reviewSummary = <Box component="aside" aria-label="선택한 휴가" sx={{
    display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 3fr) minmax(0, 2fr)' },
    gap: `${designTokens.space.md}px`,
  }}>
    <Box ref={reviewSectionRef} component="section" aria-label="이번 신청" sx={{ minWidth: 0, p: `${designTokens.space.md}px`, scrollMarginBlockStart: `${designTokens.space.md}px`, border: '1px solid', borderColor: designTokens.color.divider, borderRadius: designTokens.radius.navigation }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: `${designTokens.space.xs}px`, mb: `${designTokens.space.sm}px` }}>
        <Typography component="h3" ref={reviewTitleRef} tabIndex={-1} variant="body2" sx={{ fontWeight: 600, '&:focus-visible': createFocusRingStyles() }}>이번 신청</Typography>
        <StatusBadge label={submitLoading ? '요청 중' : '제출 전'} tone="neutral" />
      </Stack>
      <DefinitionList items={[
        ['휴가 유형', timeOffType?.descriptor || '휴가'],
        ['기간', periodLabel(startDate, endDate)],
        ['사용 단위', usageLabel],
        ['사용 예정', usageQuantity],
        ...conditionFactsSummary(conditionFacts),
        ...(reason.trim() ? [[timeOffType?.reasonRequired ? '상세 사유' : '휴가 메모', reason] as [string, string]] : []),
        ...(needsEvidence ? [['증빙 문서', evidenceDocuments.trim() ? <Stack spacing={1}>
          {evidenceDocuments.split(',').filter(Boolean).map((id) => {
            const document = evidenceSelection?.documents.find((item) => item.id === id);
            return <AttachmentItem key={id} name={document?.displayName ?? '확인하지 못한 첨부'} description={evidenceVerified && document?.scanStatus === 'CLEAN' ? '보안 검사 완료' : evidenceVerificationMessage || '증빙 확인 필요'} />;
          })}
          {!savedRequest && <Button size="small" variant="text" disabled={controlsLocked} onClick={() => changeStep('dates', 'evidence')} sx={{ alignSelf: 'flex-start' }}>증빙 수정</Button>}
        </Stack> : timeOffType?.evidencePolicy === 'AFTER_USE' ? '사용 후 제출 예정' : '미첨부'] as [string, ReactNode]] : []),
        ['처리 방식', `${timeOffType?.approvalRequired ? '승인 후 확정' : '바로 확정'}${timeOffType?.cancellationApprovalRequired ? ' · 취소 승인 필요' : ''}`],
      ]} />
    </Box>
    <Box component="section" aria-label="휴가 현황" sx={{ minWidth: 0, p: `${designTokens.space.md}px`, bgcolor: designTokens.color.overviewSurface, borderRadius: designTokens.radius.navigation }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: `${designTokens.space.xs}px`, minHeight: designTokens.layout.valueBadgeHeight, mb: `${designTokens.space.sm}px` }}>
        <Typography component="h3" variant="body2" sx={{ fontWeight: 600 }}>휴가 현황</Typography>
        <Typography variant="caption" color="text.secondary">신청 기간 기준</Typography>
      </Stack>
      {limitBased ? <AbsenceUsageLimitSummary limits={verifiedCalculation?.usageLimits ?? []} /> : <><DefinitionList items={[
        ['잔여 휴가', quantityLabel(verifiedCalculation?.balance, summaryUnit)],
        ['기존 승인 대기', quantityLabel(verifiedCalculation?.pending, summaryUnit)],
        ['사용 가능', quantityLabel(verifiedCalculation?.available, summaryUnit)],
      ]} />
      <Box component="dl" sx={{ m: 0, mt: `${designTokens.space.sm}px`, pt: `${designTokens.space.sm}px`, borderTop: '1px solid', borderColor: designTokens.color.divider }}>
        <Box component="div" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: `${designTokens.space.xs}px` }}>
          <Typography component="dt" variant="body2">신청 후 예상 사용 가능</Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600 }}>{quantityLabel(projectedRemaining, summaryUnit)}</Typography>
        </Box>
      </Box>
      {usesAdvance && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: `${designTokens.space.xs}px` }}>선사용 포함</Typography>}</>}
    </Box>
  </Box>;

  return <DialogSurface
    open={open}
    keepMounted={retainDraft}
    onEntered={() => {
      if (workspaceRef.current?.contains(document.activeElement)) return;
      focusStep(step);
    }}
    onClose={close}
    title="휴가 신청"
    titleLeading={<CalendarMonthOutlined sx={{ color: designTokens.color.secondaryText }} />}
    ariaLabel="휴가 신청 작성"
    closeLabel="휴가 신청 닫기"
    busy={submitLoading || closeDisabled}
    size="large"
    mobilePresentation="fullscreen"
    headerContent={<FlowSteps activeStep={choosingDates ? 0 : 1} steps={['신청 정보', '신청 내용 확인']} ariaLabel="휴가 신청 단계" />}
    actions={<>
      {onDiscard && !savedRequest && <Button size="small" color="inherit" disabled={controlsLocked} onClick={onDiscard}>작성 취소</Button>}
      <Typography id={progressHintId} role="status" variant="caption" color="text.secondary" sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0, mr: 'auto', '&:empty': { display: 'none' } }}>{submitLoading ? '휴가 신청을 처리하고 있습니다.' : savedRequest ? '저장된 신청 내용으로 제출합니다.' : progressBlocker}</Typography>
      {choosingDates
      ? <Button variant="contained" aria-describedby={progressBlocker ? progressHintId : undefined} disabled={submitDisabled} onClick={() => { if (validation.validate()) changeStep('review'); }}>다음</Button>
      : <>
        <Button variant="outlined" onClick={() => changeStep('dates')} disabled={controlsLocked}>이전</Button>
        <Button variant="contained" aria-describedby={progressBlocker ? progressHintId : undefined} onClick={() => { if (validation.validate()) onSubmit(); else setStep('dates'); }} disabled={submitDisabled}>{submitLoading ? '요청 중' : timeOffType?.approvalRequired ? '승인 요청' : '휴가 신청'}</Button>
      </>}
    </>}
  >
    {calculationError && !calculationFieldError && !hasCalculationInputErrors && !savedRequest && <Alert severity="error" sx={{ mb: 1 }} action={onRetryCalculation && <Button color="inherit" disabled={calculationLoading || submitLoading} onClick={onRetryCalculation}>다시 계산</Button>}>{calculationError}</Alert>}
    <Stack component="fieldset" ref={workspaceRef} disabled={controlsLocked} data-testid="absence-request-workspace-layout" sx={{ border: 0, p: 0, m: 0, minWidth: 0, gap: `${designTokens.space.md}px` }}>
      <Box ref={validation.ref} hidden={!choosingDates}>
      <Stack spacing={2}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: `${designTokens.space.sm}px`, alignItems: 'start' }}>
          <Box ref={requestTypeRef} tabIndex={-1} sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0, scrollMarginBlockStart: `${designTokens.space.md}px` }}>
            <FormTextField select fullWidth required {...field('timeOffType')} label="휴가 유형" help={timeOffType && <AbsenceRequestPolicySummary type={timeOffType} policy={conditionPolicy} calculation={verifiedCalculation} />} value={timeOffTypes.some((type) => type.id === timeOffType?.id) ? timeOffType?.id ?? '' : ''} onChange={(event) => onTimeOffTypeChange(event.target.value)} disabled={controlsLocked}>
              {timeOffTypes.map((type) => <MenuItem key={type.id} value={type.id}>{type.descriptor}{type.availableFrom ? ` · ${dateLabel(type.availableFrom)}부터` : ''}</MenuItem>)}
            </FormTextField>
          </Box>
          <FormTextField fullWidth required {...field('startDate')} type="date" label="시작일" value={startDate} onChange={(event) => handleDateInput(event.target.value, 'start')} disabled={controlsLocked} slotProps={{ htmlInput: { min: timeOffType?.availableFrom } }} />
          <FormTextField fullWidth required {...field('endDate')} type="date" label="종료일" value={endDate} onChange={(event) => handleDateInput(event.target.value, 'end')} disabled={controlsLocked} slotProps={{ htmlInput: { min: startDate || undefined } }} />
        </Box>
        {(supportsHalfDay || normalizedUnit.includes('HOUR')) && <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: `${designTokens.space.sm}px` }}>
        {supportsHalfDay && <Tooltip title={supportsQuarterDay ? '반차 경계 14:30 · 오전 반반차 근무 시작–11:30 · 오후 반반차 16:30–근무 종료' : '오전 반차: 근무 시작–14:30 · 오후 반차: 14:30–근무 종료'} describeChild>
          <Field.Root controlId={dayPartControlId} invalid={field('dayPart').error} sx={{ minWidth: 0, gridColumn: '1 / -1' }}>
          <Field.Label>사용 단위</Field.Label>
          <Box id={dayPartControlId} tabIndex={-1} aria-invalid={field('dayPart').error || undefined} aria-describedby={field('dayPart').error ? `${dayPartControlId}-message` : undefined} onBlur={field('dayPart').onBlur}>
          <SegmentedControl<AbsenceDayPart> ariaLabel="사용 단위" value={selectedHalfDay && dayPart && absenceDayPartQuantity(dayPart) !== undefined ? dayPart : 'FULL_DAY'} disabled={controlsLocked} items={[
            { value: 'FULL_DAY', label: '종일' },
            { value: 'AM', label: '오전 반차', disabled: !canUseHalfDay },
            { value: 'PM', label: '오후 반차', disabled: !canUseHalfDay },
            ...(supportsQuarterDay ? [{ value: 'AM_QUARTER' as const, label: '오전 반반차', disabled: !sameDay }, { value: 'PM_QUARTER' as const, label: '오후 반반차', disabled: !sameDay }] : []),
          ]} onChange={(value) => { if (value === 'FULL_DAY') onUseAutomaticQuantity(); else onDayPartChange(value); }} />
          </Box>
          {field('dayPart').helperText && <Field.Message>{field('dayPart').helperText}</Field.Message>}
          </Field.Root>
        </Tooltip>}
        {normalizedUnit.includes('HOUR') && <FormTextField fullWidth required {...field('quantity')} type="number" label="사용 시간" unit="시간" value={quantity} onChange={(event) => onQuantityChange(event.target.value)} sx={{ gridColumn: '1 / -1' }} slotProps={{ htmlInput: { min: timeOffType?.minimumIncrement || 0.25, step: timeOffType?.minimumIncrement || 0.25 } }} />}
        </Box>}
        <Box sx={{ containerType: 'inline-size', minWidth: 0 }}>
          <Box sx={{ mb: `${designTokens.space.sm}px`, display: 'grid', gridTemplateColumns: { xs: 'auto 1fr', sm: 'auto minmax(0, 1fr) auto' }, gridTemplateAreas: { xs: '"today navigation" "summary summary"', sm: '"today summary navigation"' }, columnGap: `${designTokens.space.md}px`, rowGap: `${designTokens.space.xs}px`, alignItems: 'center' }}>
            <Button size="small" variant="outlined" startIcon={<TodayOutlined />} sx={{ gridArea: 'today' }} onClick={() => setVisibleMonth(monthStart(localToday()))}>오늘</Button>
            <Box sx={{ gridArea: 'summary', minWidth: 0 }}>{requestSummary}</Box>
            <Stack direction="row" sx={{ gridArea: 'navigation', justifyContent: 'flex-end' }}>
              <Tooltip title="이전 달"><IconButton size="small" aria-label="이전 달" onClick={() => setVisibleMonth((month) => addMonths(month, -1))}><ChevronLeftOutlined /></IconButton></Tooltip>
              <Tooltip title="다음 달"><IconButton size="small" aria-label="다음 달" onClick={() => setVisibleMonth((month) => addMonths(month, 1))}><ChevronRightOutlined /></IconButton></Tooltip>
            </Stack>
          </Box>
          {open && <RequestCalendars port={port} fixtureMode={fixtureMode} visibleMonth={visibleMonth} startDate={startDate} endDate={endDate} onSelect={handleSelectDate} onNavigate={(date) => setVisibleMonth(monthStart(date))} />}
          {selectingEnd && <Typography role="status" variant="caption" color="text.secondary" sx={{ display: 'block', mt: `${designTokens.space.xs}px` }}>종료일을 선택해 주세요. 하루만 신청하려면 같은 날짜를 선택하거나 다음으로 진행하세요.</Typography>}
        </Box>
        <Stack spacing={2}>
          <Box hidden={!hasRequestCondition}><Stack spacing={2}>
            <AbsenceRequestConditionFields embedded policy={conditionPolicy} facts={conditionFacts} onChange={onConditionFactsChange} startDate={startDate} endDate={endDate} check={hasCalculationInputErrors ? undefined : verifiedCalculation?.conditionCheck} validationField={field} disabled={controlsLocked} />
          </Stack></Box>
          <Box>
            {timeOffType?.reasonRequired ? <FormTextField fullWidth {...field('reason')} label="상세 사유" value={reason} characterCount={{ current: reason.length, max: 4000 }} onChange={(event) => onReasonChange(event.target.value)} required multiline minRows={2} slotProps={{ htmlInput: { maxLength: 4000 } }} />
              : <AbsenceSettingsDisclosure label="메모 추가 (선택)" defaultOpen={Boolean(reason)}><FormTextField fullWidth label="휴가 메모 (선택)" value={reason} characterCount={{ current: reason.length, max: 4000 }} onChange={(event) => onReasonChange(event.target.value)} multiline minRows={2} slotProps={{ htmlInput: { maxLength: 4000 } }} /></AbsenceSettingsDisclosure>}
          </Box>
          <Box ref={evidenceRef} tabIndex={-1} hidden={!needsEvidence} sx={{ scrollMarginBlockStart: `${designTokens.space.md}px` }}>{evidenceField?.(field('evidenceDocuments'))}</Box>
        </Stack>
      </Stack>
      </Box>
      {!choosingDates && <Stack component="section" aria-label="신청 내용 확인" spacing={2}>
        {submitError && <Alert severity="error">{submitError}</Alert>}
        {reviewSummary}
        {verifiedCalculation?.conditionCheck && <AbsenceConditionCheckSummary check={verifiedCalculation.conditionCheck} />}
      </Stack>}
    </Stack>
  </DialogSurface>;
}
